import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  Grid,
  Alert,
  Skeleton,
} from '@mui/material';
import { Refresh, Search, Shield } from '@mui/icons-material';
import { superAdminApi } from '../../../api/superAdminApi';
import { SecurityEventDto } from '../../../types/superAdminTypes';

const SecurityMonitoringPage: React.FC = () => {
  const [events, setEvents] = useState<SecurityEventDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventType, setEventType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await superAdminApi.getSecurityEvents({
        eventType: eventType || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setEvents(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load security events');
    } finally {
      setLoading(false);
    }
  }, [eventType, fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Security Monitoring</Typography>
          <Typography variant="body2" color="text.secondary">Monitor authentication and security events</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" label="Event Type" value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder="e.g., LoginAttempt" />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button fullWidth variant="outlined" startIcon={<Search />} onClick={fetchData}>Search</Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>IP</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 7 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            ) : events.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No security events found</Typography></TableCell></TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell><Typography variant="body2">{new Date(event.timestamp).toLocaleString()}</Typography></TableCell>
                  <TableCell><Chip label={event.eventType} size="small" variant="outlined" icon={<Shield sx={{ fontSize: 14 }} />} /></TableCell>
                  <TableCell>{event.userName || '—'}</TableCell>
                  <TableCell>{event.tenantName || '—'}</TableCell>
                  <TableCell>
                    <Chip label={event.isSuccess ? 'Success' : 'Failed'} color={event.isSuccess ? 'success' : 'error'} size="small" />
                  </TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{event.ipAddress || '—'}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {event.details || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SecurityMonitoringPage;
